import { Theme, createTheme } from "@mui/material/styles";

enum COLORS {
    PRIMARY_GREEN = "#6CCA98",
    BLACK = "#000000",
    PRIMARY_MAGENTA = "#863399",
    LIGHT_GREEN = "#62cbc9",
    WHITE = "#FFFFFF",
    GRAY = "#eceff4",
    DARK_GRAY = "#616a78",
    LIGHT_BLACK = "#1c274c",
    DARK_GRAY_SCROLL = "#444444",
    LIGHT_COLOR_SCROLL = "#ecf2fa",
    TEAL_GREEN = "#41795b",
    SEA_GREEN = "#56a17a",
    CHARCOAL_GRAY = "#21272a",
    MATTERHORN = "#4e4e4e",
    OXFORD_BLUE = "#0f1b4d",
    BLIZZARD_BLUE = "#a1b1c8",
    RED = "#f00",
    MISTY_BLUE = "#a1b2c8",
    DARK_RED = "#c9372c",
}

export type MyTheme = Theme & {
    palette: {
        otherFeatures: {
            titleColor: string;
            bgColor: string;
            textColor: string;
        };
        history: {
            titleColor: string;
            bgColor: string;
            textColor: string;
        };
        search: {
            iconColor: string;
        };
        sidebar: {
            bgColor: string;
            textColor: string;
            activeLineColor: string;
            inActiveLineColor: string;
            iconColor: string;
        };
        announcement: {
            primaryColor: string;
        };
        footer: {
            textColor: string;
        };
        dropdown: {
            textColor: string;
            listColor: string;
            selectColor: string;
        };
        collapsibleFeatures: {
            textColor: string;
            bgColor: string;
        };
        detailsPane: {
            textColor: string;
            borderBottom: string;
            covidTextColor: string;
        };
        historicalSnapshsot: {
            textColor: string;
        };
        sublists: {
            textColor: string;
        };
        listsTable: {
            dropdownColor: string;
            sortIconColor: string;
            checkBoxColor: string;
            labelTextColor: string;
            selectTextColor: string;
            textColor: string;
            redTextColor: string;
            pagination: string;
        };
        trackedListsTable: {
            starIconColor: string;
            textColor: string;
        };
        faqSection: {
            borderColor: string;
            textColor: string;
        };
        tooltip: {
            textColor: string;
        };
    };
};

export interface IWithThemeProps {
    theme?: MyTheme;
}

export interface IWithClassName {
    className?: string;
}

export const scrollStyle = (width: string, isTableView?: boolean) => ({
    "&::-webkit-scrollbar-track": {
        boxShadow: `inset 0 0 10px 10px ${COLORS.LIGHT_COLOR_SCROLL}`,
        ...(!isTableView && {
            border: "solid 5px transparent",
            borderRadius: 20,
        }),
    },

    "&::-webkit-scrollbar-thumb": {
        boxShadow: `inset 0 0 10px 10px ${COLORS.DARK_GRAY_SCROLL}`,
        border: "solid 5px transparent",
        borderRadius: 20,
    },

    "&::-webkit-scrollbar": {
        width,
    },
});

export const defaultTheme = createTheme(
    {
        breakpoints: {
            values: {
                xs: 300,
                sm: 600,
                md: 900,
                lg: 1200,
                xl: 1536,
            },
        },
        palette: {
            action: {
                active: COLORS.PRIMARY_MAGENTA,
            },
            background: {
                default: COLORS.WHITE,
            },
            text: {
                primary: COLORS.LIGHT_BLACK,
            },
        },
        typography: {
            fontFamily: "var(--font-family)",
        },
        components: {
            MuiAutocomplete: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        color: COLORS.BLACK,
                        fontWeight: 600,
                        fontSize: 18,
                        textAlign: "center",
                        padding: theme?.spacing(1.2),
                    }),

                    listbox: ({ theme }) => scrollStyle(theme?.spacing(2)),

                    paper: {
                        borderBottom: `11px solid ${COLORS.PRIMARY_GREEN}`,
                        borderRadius: 0,
                    },

                    clearIndicator: {
                        right: 24,
                        top: -1.44,
                    },
                },
            },
            MuiContainer: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        [theme.breakpoints.up("sm")]: {
                            padding: theme.spacing(0),
                        },
                    }),
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        boxShadow: "none",
                        borderRadius: 20,
                        backgroundColor: COLORS.LIGHT_COLOR_SCROLL,
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: ({ theme }) => ({
                        borderRadius: 0,
                        borderBottom: `11px solid ${COLORS.PRIMARY_GREEN}`,
                        overflow: "auto",

                        "&.MuiPaper-root": {
                            boxShadow: "none",
                        },
                        ".MuiMenuItem-root": {
                            color: COLORS.MATTERHORN,
                        },
                        ...scrollStyle(theme?.spacing(2)),
                        ".ReactVirtualized__List": scrollStyle(
                            theme?.spacing(2),
                        ),
                    }),
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 20,
                        boxShadow: "none",
                        "&:hover": {
                            borderColor: COLORS.PRIMARY_MAGENTA,
                        },
                    },
                },
                variants: [
                    {
                        props: { variant: "outlined", color: "primary" },
                        style: {
                            "&:hover": {
                                backgroundColor: COLORS.PRIMARY_MAGENTA,
                                color: COLORS.WHITE,
                            },
                        },
                    },
                    {
                        props: { variant: "outlined", color: "secondary" },
                        style: {
                            fontSize: 18,
                            fontWeight: 600,
                            "&:hover": {
                                backgroundColor: COLORS.PRIMARY_MAGENTA,
                                color: COLORS.WHITE,
                            },
                        },
                    },
                    {
                        props: { variant: "contained", color: "primary" },
                        style: {
                            backgroundColor: COLORS.PRIMARY_MAGENTA,
                            fontSize: 18,
                            fontWeight: 600,
                            "&:hover": {
                                backgroundColor: COLORS.PRIMARY_MAGENTA,
                            },
                        },
                    },
                    {
                        props: { variant: "contained", color: "secondary" },
                        style: {
                            "&:hover": {
                                backgroundColor: COLORS.LIGHT_BLACK,
                                color: COLORS.WHITE,
                            },
                        },
                    },
                    {
                        props: { variant: "contained", color: "success" },
                        style: {
                            backgroundColor: COLORS.PRIMARY_GREEN,
                            "&:hover": {
                                backgroundColor: COLORS.TEAL_GREEN,
                            },
                        },
                    },
                ],
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderColor: COLORS.PRIMARY_MAGENTA,
                        color: COLORS.PRIMARY_MAGENTA,
                    },
                },
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        color: COLORS.PRIMARY_GREEN,
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        "& a": {
                            color: COLORS.PRIMARY_MAGENTA,
                            textDecoration: "none",
                        },
                    },
                },
            },
            MuiTable: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        [theme.breakpoints.up("sm")]: {
                            padding: theme.spacing(0),
                        },
                        backgroundColor: COLORS.LIGHT_COLOR_SCROLL,
                    }),
                },
            },
            MuiTableHead: {
                styleOverrides: {
                    root: {
                        "& > .MuiTableRow-root:first-of-type": {
                            backgroundColor: COLORS.MISTY_BLUE,
                            ".MuiTableCell-root": {
                                backgroundColor: COLORS.MISTY_BLUE,
                            },
                        },
                        "& .MuiTableCell-root": {
                            overflowX: "visible",
                        },
                    },
                },
            },
            MuiTableBody: {
                styleOverrides: {
                    root: {
                        backgroundColor: COLORS.LIGHT_COLOR_SCROLL,
                        "& > .MuiTableRow-root:last-child > .MuiTableCell-root":
                            {
                                borderBottom: `1px solid ${COLORS.BLIZZARD_BLUE}`,
                            },
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        overflowX: "auto",
                        borderBottom: `1px solid ${COLORS.BLIZZARD_BLUE}`,
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        "&:last-child td": {
                            borderBottom: "none",
                        },
                        "&:last-child td:first-of-type": {
                            borderBottomLeftRadius: 20,
                        },
                        "&:last-child td:last-child": {
                            borderBottomRightRadius: 20,
                        },
                    },
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        width: "105px",
                        height: "12px",
                        borderRadius: "5px",
                        backgroundColor: COLORS.MATTERHORN,
                    },
                    bar: {
                        backgroundColor: COLORS.LIGHT_GREEN,
                    },
                    determinate: {
                        transition: "transform .4s linear",
                    },
                    buffer: {
                        backgroundColor: "transparent",
                    },
                },
            },
            MuiTypography: {
                styleOverrides: {
                    caption: {
                        fontSize: 12,
                        fontWeight: 800,
                    },
                },
            },
            MuiAccordion: {
                styleOverrides: {
                    root: {
                        boxShadow: "none",
                        backgroundColor: COLORS.LIGHT_COLOR_SCROLL,
                        margin: "0 !important",
                        border: "none",
                        borderBottom: `1px solid ${COLORS.BLIZZARD_BLUE}`,
                        "&:before": {
                            display: "none",
                        },
                    },
                },
            },
            MuiAccordionSummary: {
                styleOverrides: {
                    root: {
                        display: "flex",
                        alignItems: "center",
                        "& > .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root":
                            {
                                color: COLORS.PRIMARY_MAGENTA,
                            },
                    },
                    expandIconWrapper: {
                        transition: "none",
                    },
                },
            },
        },
    },
    {
        palette: {
            otherFeatures: {
                titleColor: COLORS.LIGHT_BLACK,
                bgColor: COLORS.LIGHT_GREEN,
                textColor: COLORS.WHITE,
            },
            history: {
                titleColor: COLORS.LIGHT_BLACK,
                bgColor: COLORS.GRAY,
                textColor: COLORS.DARK_GRAY,
            },
            search: {
                iconColor: COLORS.PRIMARY_GREEN,
            },
            sidebar: {
                bgColor: COLORS.WHITE,
                textColor: COLORS.TEAL_GREEN,
                activeLineColor: COLORS.SEA_GREEN,
                inActiveLineColor: COLORS.TEAL_GREEN,
                iconColor: COLORS.TEAL_GREEN,
            },
            announcement: {
                primaryColor: COLORS.DARK_GRAY,
            },
            footer: {
                textColor: COLORS.CHARCOAL_GRAY,
            },
            dropdown: {
                textColor: COLORS.MATTERHORN,
                listColor: COLORS.MATTERHORN,
                selectColor: COLORS.PRIMARY_GREEN,
            },
            collapsibleFeatures: {
                bgColor: COLORS.LIGHT_COLOR_SCROLL,
                textColor: COLORS.MATTERHORN,
            },
            detailsPane: {
                textColor: COLORS.MATTERHORN,
                borderBottom: COLORS.BLIZZARD_BLUE,
                covidTextColor: COLORS.RED,
            },
            historicalSnapshsot: {
                textColor: COLORS.MATTERHORN,
            },
            sublists: {
                textColor: COLORS.MATTERHORN,
            },
            listsTable: {
                dropdownColor: COLORS.BLIZZARD_BLUE,
                sortIconColor: COLORS.LIGHT_BLACK,
                checkBoxColor: COLORS.LIGHT_GREEN,
                labelTextColor: COLORS.LIGHT_BLACK,
                selectTextColor: COLORS.DARK_GRAY,
                textColor: COLORS.MATTERHORN,
                redTextColor: COLORS.DARK_RED,
                pagination: COLORS.DARK_GRAY,
            },
            trackedListsTable: {
                starIconColor: COLORS.PRIMARY_MAGENTA,
                textColor: COLORS.MATTERHORN,
            },
            faqSection: {
                borderColor: COLORS.BLIZZARD_BLUE,
                textColor: COLORS.MATTERHORN,
            },
            tooltip: {
                textColor: COLORS.WHITE,
            },
        },
    } as MyTheme,
);
